import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import { SearchableSelectStyles as S } from './SearchableSelect.styles';

export interface SearchableSelectOption {
  text: string;
  value: string;
}

type SearchableBaseProps = {
  showBottomBorder?: boolean;
  options: SearchableSelectOption[];
  selectedValue: string | undefined;
  placeholder: string;
  onChange: (value: string) => void;
};

type OptionalSearchProps =
  | {
      showSearch: true;
      noResultsText: string;
    }
  | {
      showSearch?: false;
    };

type OptionalFloatingLabelProps =
  | {
      selectAValueText: string;
      selectedValueText: string;
    }
  | {
      selectAValueText?: never;
      selectedValueText?: never;
    };

export type SearchableSelectProps = SearchableBaseProps & OptionalSearchProps & OptionalFloatingLabelProps;

const SearchableSelect: FunctionComponent<SearchableSelectProps> = (props) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const selectedOption = useMemo(() => props.options.find((x) => x.value === props.selectedValue), [props.options]);
  const searchRef = useRef<HTMLInputElement>(null);
  useClickAway(searchRef, () => setIsOpen(false), ['click']);
  const results = useMemo(() => {
    if (searchTerm === '') {
      return props.options;
    }

    return props.options.filter((x) => x.text.toUpperCase().indexOf(searchTerm.toUpperCase()) > -1);
  }, [searchTerm, props.options, props.selectedValue]);

  useEffect(() => {
    if (!isOpen || !searchRef.current) {
      setSearchTerm('');
      return;
    }

    searchRef.current.focus();
  }, [isOpen]);

  const handleItemClick = (value: string): void => {
    props.onChange(value);
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <S.Wrapper
      showBottomBorder={props.showBottomBorder}
      isOpen={isOpen}
      onClick={(e) => {
        e.stopPropagation();
        setIsOpen(!isOpen);
      }}
    >
      <S.Label>{props.selectedValue ? props.selectedValueText : props.selectAValueText}</S.Label>
      {selectedOption ? (
        <S.Value>{selectedOption.text}</S.Value>
      ) : (
        <S.Value data-is-placeholder isPlaceholder>
          {props.placeholder}
        </S.Value>
      )}

      <S.Dropdown isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
        {props.showSearch ? (
          <S.Search
            ref={searchRef}
            placeholder={props.placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
          />
        ) : null}
        <S.Options>
          {props.showSearch && results.length === 0 ? (
            <S.NoResults>{props.noResultsText}</S.NoResults>
          ) : (
            results.map((x) => (
              <S.Option
                key={x.value}
                selected={props.selectedValue === x.value}
                onClick={() => handleItemClick(x.value)}
              >
                {x.text}
              </S.Option>
            ))
          )}
        </S.Options>
      </S.Dropdown>
    </S.Wrapper>
  );
};

export default SearchableSelect;
